import { api } from "./client";

/**
 * The Broker intro tab's text, web form and follow-ups (D24–D33) — the client
 * of `api/buyer-submissions/{id}/broker-outreach` and of the fallback wording
 * editor at `api/broker-outreach/wording`.
 *
 * <p>A module of its own rather than more surface on `buyers.ts`, like
 * `buyerSendVersions.ts`: these routes are read by one tab.</p>
 *
 * <p>🔴 <b>Phoenix sends nothing on the text and web-form channels.</b> The
 * consultant copies, sends from their own phone or the broker's form, and LOGS
 * what they sent; each log writes one send version and one communications entry
 * (placed on the buyer, the submission and the listing) and moves nothing on the
 * buyer.</p>
 */

/** The route segment for a channel. */
export type BrokerOutreachChannel = "text" | "web-form" | "follow-up-email" | "follow-up-text";

export type BrokerOutreachCardStatus = "NotStarted" | "DraftSaved" | "Sent";

/** How a card's latest send went (D25). */
export type BrokerOutreachSentVia = "AppSend" | "OwnEmail" | "CopiedText" | "WebForm";

export interface BrokerOutreachReadinessDto {
  hasAgent: boolean;
  /** The agent's display name — "Firm contact — {Firm}" for a firm contact — or
   *  "No agent". */
  agentLabel: string;
  isFirmContact: boolean;
  agentEmail: string | null;
  hasEmail: boolean;
  agentPhone: string | null;
  hasPhone: boolean;
  phoneType: "Mobile" | "Office" | "Unknown";
  contactFormUrl: string | null;
  hasContactForm: boolean;
  /** True when the email or the phone is missing (never the contact form, which
   *  research does not look for) — the strip then offers FIND AGENT. */
  somethingMissing: boolean;
  /** `/listings/{id}/agent-research` (R6). Opened in a NEW TAB from here. */
  researchPath: string;
}

export interface BrokerOutreachSenderDto {
  userId: string | null;
  fullName: string;
  firstName: string;
  email: string | null;
  phone: string | null;
  /** False when the buyer has no consultant and the drafts fall back to the
   *  acting user. */
  isConsultant: boolean;
  writtenAsDiffers: boolean;
  /** Server-composed card note, or null. */
  note: string | null;
}

export interface BrokerOutreachFieldKitDto {
  fullName: string;
  email: string | null;
  phone: string | null;
  company: string;
  subject: string;
}

export interface BrokerOutreachDraftDto {
  body: string;
  subject: string | null;
  characterLimit: number | null;
  generatedAtUtc: string;
  editedAtUtc: string | null;
  /** True when the fallback template wrote it, not the assistant. */
  fromTemplate: boolean;
  aiModel: string | null;
  /** The follow-up email only: body + sign-off + role statement, as the broker
   *  reads them — what COPY puts on the clipboard. */
  copyText: string | null;
  signatureBlock?: string | null;
  roleStatement?: string | null;
  /** The follow-up email only (WSA): the locked listing-link line, already
   *  inside `copyText`; null when the listing has no URL that qualifies. */
  listingLinkLine?: string | null;
}

export interface BrokerOutreachCardDto {
  channel: "Email" | "Text" | "WebForm" | "FollowUpEmail" | "FollowUpText";
  status: BrokerOutreachCardStatus;
  sentAtUtc: string | null;
  sentByName: string | null;
  sentVia: BrokerOutreachSentVia | null;
  writtenAsName: string | null;
  available: boolean;
  /** Why the card cannot be used, as readable text (D25: no card is locked). */
  unavailableReason: string | null;
  hint: string | null;
  draft: BrokerOutreachDraftDto | null;
}

export interface BrokerOutreachStateDto {
  submissionId: string;
  listingId: string;
  readiness: BrokerOutreachReadinessDto;
  sender: BrokerOutreachSenderDto;
  fieldKit: BrokerOutreachFieldKitDto;
  email: BrokerOutreachCardDto;
  text: BrokerOutreachCardDto;
  webForm: BrokerOutreachCardDto;
  followUpEmail: BrokerOutreachCardDto;
  followUpText: BrokerOutreachCardDto;
  webFormDefaultLimit: number;
  webFormMinimumLimit: number;
  textTargetCharacters: number;
  followUpEmailMaxWords: number;
  canEditWording: boolean;
}

export interface LogBrokerOutreachPayload {
  /** The exact text that was copied and sent. */
  body: string;
  formUrl?: string | null;
  characterLimit?: number | null;
}

export interface RecordBrokerEmailSendPayload {
  /** The exact subject and body COPY placed on the clipboard (R4). */
  subject: string;
  body: string;
}

export interface BrokerOutreachWordingDto {
  key: string;
  name: string;
  description: string;
  defaultText: string;
  publishedText: string | null;
  allowedTokens: string[];
  version: number;
  publishedAtUtc: string | null;
}

const base = (id: string) => `/buyer-submissions/${id}/broker-outreach`;

export const brokerOutreachApi = {
  get: (id: string) => api.get<BrokerOutreachStateDto>(base(id)),

  /** Drafts, redrafts or SHORTENS TO FIT (the web form's current limit). One
   *  model call — rate-limited server-side (429). */
  draft: (id: string, channel: BrokerOutreachChannel, characterLimit?: number | null) =>
    api.post<BrokerOutreachStateDto>(`${base(id)}/${channel}/draft`, { characterLimit: characterLimit ?? null }),

  saveDraft: (id: string, channel: BrokerOutreachChannel, body: string, characterLimit?: number | null) =>
    api.put<BrokerOutreachStateDto>(`${base(id)}/${channel}/draft`, { body, characterLimit: characterLimit ?? null }),

  /** LOG / I SUBMITTED IT — the text, the web form or the follow-up text. */
  log: (id: string, channel: Exclude<BrokerOutreachChannel, "follow-up-email">, payload: LogBrokerOutreachPayload) =>
    api.post<BrokerOutreachStateDto>(`${base(id)}/${channel}/log`, payload),

  sendFollowUpEmail: (id: string) =>
    api.post<BrokerOutreachStateDto>(`${base(id)}/follow-up-email/send`),

  recordFollowUpEmail: (id: string, payload: RecordBrokerEmailSendPayload) =>
    api.post<BrokerOutreachStateDto>(`${base(id)}/follow-up-email/record-send`, payload),

  listWording: () => api.get<BrokerOutreachWordingDto[]>("/broker-outreach/wording"),

  publishWording: (key: string, text: string) =>
    api.put<BrokerOutreachWordingDto>(`/broker-outreach/wording/${encodeURIComponent(key)}`, { text }),

  resetWording: (key: string) =>
    api.delete<BrokerOutreachWordingDto>(`/broker-outreach/wording/${encodeURIComponent(key)}`),
};
